import { deviceSession } from '@/privacy/deviceStorage';
import { RoutePendingFrame } from './RoutePendingFrame';
import {
  lazy,
  Suspense,
  useEffect,
  useSyncExternalStore,
  type ComponentType,
  type LazyExoticComponent,
} from 'react';

/**
 * A route's code, loaded so that one failed request never takes the app down (I-0678).
 *
 * Every signed-in screen is its own chunk. A plain `lazy()` has two ways to fail that users meet:
 * the network drops for a moment while the chunk is fetched, or a deploy has replaced the hashed
 * chunk the open tab still asks for. Either way `lazy()` rejects, nothing catches it, and React
 * Router draws its developer error screen over the whole window.
 *
 * So the load is retried once. The retry asks for the chunk under a fresh address: a browser keeps a
 * failed module import and answers the same address with the same failure without asking the
 * network again (measured in Chromium, I-0678, and in WebKit, I-1254), so importing it a second time
 * as written retries nothing. If the retry fails too and the chunk is gone from the server — a 404,
 * or the SPA fallback answering with HTML where a script was asked for — the tab is running an old
 * build, and one reload fetches the new one. The reload is claimed per chunk address in session
 * storage first, so a chunk that is still missing after it is reported rather than reloaded again:
 * that is what keeps a broken deploy from becoming a reload loop. A server that answers nothing at
 * all, as for the few seconds a deploy swaps the web container, is waited for: the chunk is asked
 * for again after 0.5, 1, 2, 4 and 8 s while the browser is online (I-2358). Anything else is thrown as a
 * `RouteChunkLoadError`, which `RouteLoadError` draws inside the shell with Try again.
 */
export class RouteChunkLoadError extends Error {
  constructor(
    readonly cause: unknown,
    /** Forgets the failed load, so the next render asks the network again. */
    readonly retry: () => void,
    /** The screen's chunk, when known: Try again refreshes what it imports before reloading. */
    readonly url: string | null = null,
  ) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = 'RouteChunkLoadError';
  }
}

export type ChunkPresenceType = 'missing' | 'present' | 'unknown';

/** What the recovery needs from the browser, so it can be tested without one. */
export interface IChunkRecovery {
  /** Imports the module at `url` again, past the browser's memory of the failure. */
  importAgain(url: string): Promise<unknown>;
  /** The address a route's loader imports, for a failure that names none (I-1254). */
  addressOf(load: () => Promise<unknown>): string | null;
  probe(url: string): Promise<ChunkPresenceType>;
  reload(): void;
  storage: Pick<Storage, 'getItem' | 'setItem'> | null;
  /**
   * Whether the page is being left (I-1128). Its pending imports are then ended by the browser, and
   * a failure among them is not this screen's to report: WebKit rejects them as the page goes, and
   * the route error screen was logged by a page the reader had already left.
   */
  leaving(): boolean;
  /** Resolves after `ms` (I-2358). The browser's own timer when not given. */
  wait?(ms: number): Promise<void>;
  /** Whether the browser knows it has no network: waiting for the server cannot help then. */
  offline?(): boolean;
}

/**
 * I-2358: the waits before each further import while the server answers nothing, about 15 s in all,
 * longer than a stable deploy's container swap (Jay met the route error at 08:15 on 2026-10-31, for
 * a chunk that answered 200 after it).
 */
export const SERVER_AWAY_WAITS_MS = [500, 1000, 2000, 4000, 8000] as const;

const waitFor = (recovery: IChunkRecovery, ms: number): Promise<void> =>
  recovery.wait
    ? recovery.wait(ms)
    : new Promise((resolve) => {
        setTimeout(resolve, ms);
      });

const RELOADED_FOR_KEY = 'featit:chunk-reloaded-for';

/**
 * The chunk address a failed dynamic import names. Chrome and Firefox put it in the message; Vite's
 * CSS preload does too. Safari names none; the address is then the loader's own (`importedAddressOf`).
 */
export function chunkUrlOf(error: unknown): string | null {
  const message = error instanceof Error ? error.message : String(error ?? '');
  const match = /(https?:\/\/[^\s'"]+|\/assets\/[^\s'"]+)/.exec(message);
  return match ? match[1] : null;
}

/**
 * The address a loader imports, read from its own text: the one `import("…")` in it, resolved
 * against `base`, the address of the module the loader sits beside (I-1254). Dev's loader names
 * the source module from the root (`import("/src/features/roadmap/RoadmapPage.tsx")`); the bundle's
 * names the hashed chunk next to its own (`import("./RoadmapPage-6zH4otUU.js")`, wrapped in Vite's
 * preload), and every chunk is built into one directory. A loader with no import, or more than one,
 * names no address.
 */
export function importedAddressOf(load: () => Promise<unknown>, base: string): string | null {
  const imports = [...String(load).matchAll(/\bimport\(\s*(["'`])([^"'`]+)\1\s*\)/g)];
  if (imports.length !== 1) return null;
  try {
    return new URL(imports[0][2], base).href;
  } catch {
    return null;
  }
}

/** Records that this chunk has had its reload. `false` when it already had one, or cannot be recorded. */
export function claimReload(storage: IChunkRecovery['storage'], url: string): boolean {
  if (!storage) return false;
  try {
    const reloaded: string[] = JSON.parse(storage.getItem(RELOADED_FOR_KEY) ?? '[]');
    if (reloaded.includes(url)) return false;
    storage.setItem(RELOADED_FOR_KEY, JSON.stringify([...reloaded, url]));
    return true;
  } catch {
    // A storage that cannot remember the reload cannot guard it either, so there is none.
    return false;
  }
}

export async function loadWithRecovery<T>(
  load: () => Promise<T>,
  recovery: IChunkRecovery,
  retry: () => void,
): Promise<T> {
  /** A page being left reports nothing: it settles never, as the page is going away anyway. */
  const leftBehind = () => new Promise<T>(() => undefined);
  try {
    return await load();
  } catch (first) {
    if (recovery.leaving()) return leftBehind();
    // WebKit's failure names no address, and asked again as written it answers from its record of
    // the failure without asking the network (I-1254): the loader's own address is asked afresh.
    const failedUrl = chunkUrlOf(first) ?? recovery.addressOf(load);
    const waits: readonly number[] = SERVER_AWAY_WAITS_MS;
    for (let attempt = 0; ; attempt += 1) {
      try {
        // Without an address there is nothing to ask for afresh; asking as written is all there is.
        return failedUrl ? ((await recovery.importAgain(failedUrl)) as T) : await load();
      } catch (error) {
        // The first failure's address, not the retry's: the retry's carries a fresh query each time,
        // and a guard keyed on it would never recognise the chunk it already reloaded for.
        const url = failedUrl;
        const presence = url ? await recovery.probe(url) : 'unknown';
        if (url && presence === 'missing' && claimReload(recovery.storage, url)) {
          recovery.reload();
          // The page is going away; settling now would draw the error for a frame first.
          return new Promise<T>(() => undefined);
        }
        if (recovery.leaving()) return leftBehind();
        // I-2358: only a server that answers nothing is waited for, and only with an address to ask
        // afresh; a chunk that answers and still fails, or a missing one, is reported at once.
        const away = url !== null && presence === 'unknown' && !recovery.offline?.();
        if (!away || attempt >= waits.length) throw new RouteChunkLoadError(error, retry, url);
        await waitFor(recovery, waits[attempt]);
        if (recovery.leaving()) return leftBehind();
      }
    }
  }
}

function sessionStorageOrNull(): Storage | null {
  try {
    return deviceSession;
  } catch {
    return null;
  }
}

/**
 * Set when the page starts to go (`pagehide`, and `beforeunload` before it), cleared when it is shown
 * again from the back-forward cache (`pageshow`). WebKit ends a left page's loads after these fire.
 */
let pageLeaving = false;
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', () => {
    pageLeaving = true;
  });
  window.addEventListener('pagehide', () => {
    pageLeaving = true;
  });
  window.addEventListener('pageshow', () => {
    pageLeaving = false;
  });
}

export const browserChunkRecovery: IChunkRecovery = {
  leaving: () => pageLeaving,
  offline: () => typeof navigator !== 'undefined' && navigator.onLine === false,
  importAgain(url) {
    const fresh = `${url}${url.includes('?') ? '&' : '?'}retry=${Date.now()}`;
    return import(/* @vite-ignore */ fresh);
  },
  addressOf: (load) => importedAddressOf(load, import.meta.url),
  async probe(url) {
    try {
      const response = await fetch(url, { method: 'HEAD', cache: 'no-store' });
      if (response.status === 404 || response.status === 410) return 'missing';
      // An SPA fallback answers a missing script with the app's HTML and a 200.
      if (response.ok && (response.headers.get('content-type') ?? '').includes('text/html')) {
        return 'missing';
      }
      return response.ok ? 'present' : 'unknown';
    } catch {
      return 'unknown';
    }
  },
  reload: () => window.location.reload(),
  get storage() {
    return sessionStorageOrNull();
  },
};

/**
 * `lazy()`, with the recovery above. It takes the module's import and the export that is the
 * screen, rather than one loader that does both, because a retry imports the module again from a
 * fresh address and has to pick the same export out of it. Route screens take no props: everything
 * they need comes from the address. A component split the same way passes its own props through.
 */
/** A route's component that can be asked to fetch its code before it is first drawn (T-0559). */
export type LazyRouteComponent<P extends object> = ComponentType<P> & {
  /** Fetches the chunk now. Once it is in, the route draws without suspending at all. */
  preload: () => Promise<void>;
};

export function lazyRoute<M, P extends object = Record<string, never>>(
  importModule: () => Promise<M>,
  pick: (module: M) => ComponentType<P>,
  recovery: IChunkRecovery = browserChunkRecovery,
): LazyRouteComponent<P> {
  let Lazy: LazyExoticComponent<ComponentType<P>>;
  /*
   * T-0559: the component once its chunk is in. A route drawn after that renders it directly. Drawn
   * through `lazy()` it would suspend for a microtask even with the chunk in hand, the nearest
   * Suspense would show its fallback, and React holds a revealed fallback for at least 300 ms:
   * measured on the app's cold launch, that hold was the largest single cost before `ready`.
   */
  let loaded: ComponentType<P> | null = null;
  let loading: Promise<ComponentType<P>> | null = null;
  const load = (): Promise<ComponentType<P>> => {
    loading ??= loadWithRecovery(importModule, recovery, reset).then((module) => {
      loaded = pick(module);
      return loaded;
    });
    return loading;
  };
  function reset(): void {
    loading = null;
    Lazy = lazy(async () => ({ default: await load() }));
  }
  reset();
  // Props pass through, so a component that is not a route can be split the same way (T-0318).
  function LazyRoute(props: P) {
    const Loaded = loaded;
    return Loaded ? <Loaded {...props} /> : <Lazy {...props} />;
  }
  LazyRoute.preload = async (): Promise<void> => {
    try {
      await load();
    } catch {
      // A failed preload changes nothing: the route's own render asks again and shows the failure.
      reset();
    }
  };
  return LazyRoute;
}

/**
 * A route's page (F-0742, I-2056; the F-0774 dev walk): `lazyRoute` inside a Suspense boundary of its
 * own, drawing `RoutePendingFrame` while the page's code arrives.
 *
 * React Router navigates in a transition, and a transition keeps every boundary that already showed
 * its content: with the shell's one boundary around the outlet, the page the reader left stayed on
 * screen, its heading and all, until the next page's chunk was in — measured on dev at 1.0 s warm and
 * 5.5 s cold, a "no page here" card standing under the address of Settings. A new page is a new
 * element here, so its boundary is new, and a new boundary shows its fallback even in a transition.
 */
export function lazyPage<M, P extends object = Record<string, never>>(
  importModule: () => Promise<M>,
  pick: (module: M) => ComponentType<P>,
  recovery: IChunkRecovery = browserChunkRecovery,
): LazyRouteComponent<P> {
  const Route = lazyRoute(importModule, pick, recovery);
  function LazyPage(props: P) {
    return (
      <Suspense fallback={<RoutePendingFrame />}>
        <Route {...props} />
      </Suspense>
    );
  }
  LazyPage.preload = Route.preload;
  return LazyPage;
}

/**
 * A part of the shell in its own chunk (T-0559): a banner, a dialog, a panel, the sidebar's view tree.
 * Until its chunk is in it draws nothing, so the screen behind it never waits for it and no loading
 * mark flashes where it will appear; it draws in the first commit after its chunk is in. It also has
 * `preload`, for a shell that knows it will draw it, or a control that is about to open it.
 *
 * ⛔ Not through Suspense. React 19 holds the content of a boundary that suspended for 300 ms after
 * its fallback was shown, a `null` fallback too: the sidebar's Views group, loaded so, came 297–300 ms
 * after the page beside it on every measured run, its chunk in for most of that hold (batch 485). The
 * loaded component is read from a store instead. A chunk that fails for good is thrown from render,
 * to the nearest error element, as a route's is. A part that shows a fallback on purpose is a
 * `lazyRoute` inside its own `Suspense`.
 */
export function lazyOverlay<M, P extends object = Record<string, never>>(
  importModule: () => Promise<M>,
  pick: (module: M) => ComponentType<P>,
  recovery: IChunkRecovery = browserChunkRecovery,
): LazyRouteComponent<P> {
  type SnapshotType = { component: ComponentType<P> } | { error: unknown } | null;
  let snapshot: SnapshotType = null;
  let loading: Promise<void> | null = null;
  const listeners = new Set<() => void>();
  const settle = (next: SnapshotType) => {
    snapshot = next;
    listeners.forEach((listener) => listener());
  };
  const load = (): Promise<void> => {
    loading ??= loadWithRecovery(importModule, recovery, reset).then(
      (module) => settle({ component: pick(module) }),
      (error: unknown) => {
        settle({ error });
        throw error;
      },
    );
    return loading;
  };
  function reset(): void {
    loading = null;
    settle(null);
  }
  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => void listeners.delete(listener);
  };
  const read = () => snapshot;
  function Overlay(props: P) {
    const current = useSyncExternalStore(subscribe, read, read);
    useEffect(() => {
      if (current === null) load().catch(() => undefined);
    }, [current]);
    if (current && 'error' in current) throw current.error;
    const Loaded = current?.component;
    return Loaded ? <Loaded {...props} /> : null;
  }
  Overlay.preload = async (): Promise<void> => {
    try {
      await load();
    } catch {
      // A failed preload changes nothing: the part's own render asks again and shows the failure.
      reset();
    }
  };
  return Overlay;
}
